/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */
import { Component, EventEmitter, Output, ViewChild } from "@angular/core";
import { Zaa_AssetDto } from "app/models/zoneassignedassets/Zaa_AssetDto";
import { Zaa_ZonesResponseDto } from "app/models/zoneassignedassets/Zaa_ZonesResponeDto";
import { AddAssetDialogComponent } from "../add-asset-dialog/add-asset-dialog.component";
import { ZoneAssignedAssetsService } from "app/services/zone-assigned-assets.service";
import { SimpleIndefiniteProgressDialogComponent } from "app/components/simple-indefinite-progress-dialog/simple-indefinite-progress-dialog.component";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";

/** Declare $ to call bootstrap */
declare var $: any;

/**
 * Dialog component used to display the Assigned Assets for a specific Zone and allow
 * the User to add / remove to update the list. 
 */
@Component({
  selector: 'ef-zone-assigned-assets-dialog',
  templateUrl: './zone-assigned-assets-dialog.component.html',
  styleUrls: ['../../../../assets/css/infrastructure.css']
})
export class ZoneAssignedAssetsDialogComponent {

  /** Progress Dialog displayed when performing actions to give the User an indication something is happening... */
  @ViewChild(SimpleIndefiniteProgressDialogComponent)
  indefiniteProgressDialog!: SimpleIndefiniteProgressDialogComponent;
  
  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent!: SimpleSuccessErrorDialogComponent;

  /** Dialog used to view / edit the assigned assets for a specific zone */
  @ViewChild(AddAssetDialogComponent)
  addAssetDialog!: AddAssetDialogComponent;

  /** Output used to indicate to the parent component that things were changed and they might need to refresh */
  @Output()
  refreshParent = new EventEmitter<void>();

  /** Zone Assigned Asset Header whose information is being displayed / edited */
  zaa: Zaa_ZonesResponseDto | null = null;

  /** Holds the Assigned Assets that are being displayed to the User for editing */
  assignedAssets: Zaa_AssetDto[] = [];

  /** Holds the original copy of the Assigned Assets being edited.  Used to determine if any updates are needed. */
  originalAssignedAssets: Zaa_AssetDto[] = [];


  /** Constructor */
  constructor(
    private zaasService: ZoneAssignedAssetsService
  ) { }


  /** 
   * Shows the Dialog  
   */
  public show(zaa: Zaa_ZonesResponseDto, assignedAssets: Zaa_AssetDto[]): void {
    this.zaa = zaa;
    this.originalAssignedAssets = assignedAssets;
    this.assignedAssets = JSON.parse(JSON.stringify(assignedAssets));
    $("#zaaDialog").modal("show");
  }

  /**
   * Hides the Dialog
   */
  public hide(): void {
    $("#zaaDialog").modal("hide");
  }

  /**
   * Called when the "Cancel" UI Button is clicked.
   * Cancels / Hides the Dialog
   */
  onCancelClicked(): void {
    this.hide();
  }

  /**
   * Called when the "Add" UI Button is clciked.
   * Shows the "Add Asset" Dialog so the User can select an Asset to assign to the Zone.
   */
  onAddClicked(): void {
    this.addAssetDialog.show();
  }

  /**
   * Called when the "Update" UI Button is clicked.
   * Updates the Assigned Assets for the Zone.
   */
  onUpdateClicked(): void {
    if (JSON.stringify(this.originalAssignedAssets) == JSON.stringify(this.assignedAssets)) {
      console.log(`No changes detected...`);
      return;
    }

    this.indefiniteProgressDialog.show("Saving", "Saving Assigned Assets for Zone...");
    this.updateAssignedAssets();
  }

  /**
   * Updates the Assigned Assets for the Zone in the background
   */
  private async updateAssignedAssets(): Promise<void> {
    if (this.zaa?.zone?.id != null) {
      let error: any = null;
      try {
        await this.zaasService.setAssetsForZoneId(this.zaa?.zone?.id, this.assignedAssets);
      } catch (e) {
        error = e;
      }

      this.indefiniteProgressDialog.hide();
      if (error != null) {
        this.handleError(error);
      } else {
        this.hide();
        this.refreshParent.emit();
      }
    }
  }

  /** 
   * Called when the "Remove" Action Icon is clicked on a Row.
   * Removes the Asset from the displayed list.
   */
  onDeleteClicked(asset: Zaa_AssetDto): void {
    this.assignedAssets.splice(this.assignedAssets.findIndex(a => a === asset), 1);
  }

  /**
   * Called when a User selects an Asset in the Add Asset Dialog.
   * Adds the Asset to the displayed list of Assigned Assets for the Zone.
   */
  onAssetSelected(asset: Zaa_AssetDto): void {
    // If the Asset is already assigned, don't add it
    if (this.assignedAssets.find(a => a.id == asset.id) != null) {
      return;
    }

    // If the Asset is part of the original assets assigned to the zone, add the original back into it.
    const originalAsset = this.originalAssignedAssets.find(a => a.id == asset.id);
    if (originalAsset != null) {
      this.assignedAssets.push(originalAsset);
    } 
    
    // Not an original asset, add as a new Asset
    else {
      this.assignedAssets.push(asset);
    }

    // Sort the displayed list by the Asset Name
    this.assignedAssets.sort((a, b) => a.name.localeCompare(b.name));
  }
  
    /**
     * Displays the error to the User
     */
    handleError(error: any): void {
      /* New handling (TJB 04/18/2023) */
      if (error?.error != null) {
        this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
      } 
      
      /* Default back to old handling */
      else if (error != null) {
        const response: Response = error;
        alert(response.text());
      }
    }

}
